import { CONFIG } from '../config';
import { normaliseName, normalisePostcode, shardForName } from './gazetteerKeys';

// Finds a postcode, street or place in the site gazetteer, read straight from its published files (no service call), so
// someone with no documents can put the map where their site is.

export interface PlaceResult {
  label: string;
  /** What kind of place, e.g. "Postcode", "Road", "Village". */
  kind: string;
  lat: number;
  lon: number;
  /** A street or place name worth keeping as the site's name; absent for a postcode, which names an area, not a site. */
  siteName?: string;
}

const KINDS: Record<string, string> = { R: 'Road', H: 'Hamlet', V: 'Village', T: 'Town', S: 'Suburb', C: 'City', O: 'Settlement', X: 'Railway station' };
const MAX_RESULTS = 8;

let pointer: Promise<string> | null = null;

function base(): string {
  return `${(CONFIG.gazetteerUrl ?? '').replace(/\/+$/, '')}/gazetteer`;
}

async function getJson<T>(url: string): Promise<T | null> {
  const r = await fetch(url);
  if (r.status === 404 || r.status === 403) return null;
  if (!r.ok) throw new Error(`The address search is unavailable (HTTP ${r.status})`);
  return r.json() as Promise<T>;
}

function version(): Promise<string> {
  pointer ??= getJson<{ version?: string }>(`${base()}/current.json`).then((p) => {
    if (!p?.version) throw new Error('The address search is unavailable');
    return p.version;
  });
  pointer.catch(() => { pointer = null; });
  return pointer;
}

const titleCase = (s: string) => s.replace(/\b([a-z])/g, (c) => c.toUpperCase());

type NameEntry = [string, number, number, string, ...number[]];
interface NameShard { scale: number; names: Record<string, NameEntry[]> }

export interface PlaceSearch {
  results: PlaceResult[];
  /** More places share the name than are listed: adding the town after a comma narrows them. */
  more: boolean;
}

const NEAR_PLACE_M = 5_000;
const SETTLEMENTS = new Set(['H', 'V', 'T', 'S', 'C', 'O']);

async function lookUpName(v: string, key: string): Promise<{ entries: NameEntry[]; scale: number }> {
  const shard = await getJson<NameShard>(`${base()}/${v}/names/${shardForName(key)}.json.gz`);
  return { entries: shard?.names[key] ?? [], scale: shard?.scale ?? 1 };
}

function metres(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const kx = 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot((a.lon - b.lon) * kx, (a.lat - b.lat) * 110_574);
}

/** "BS32 4JZ", "Frog Lane", or "Frog Lane, Coalpit Heath" (the part after the comma narrows it to near that place). */
export async function searchPlaces(query: string): Promise<PlaceSearch> {
  const q = query.trim();
  if (!q) return { results: [], more: false };
  const v = await version();
  const pc = normalisePostcode(q);
  if (pc) {
    const [district, inward] = pc.split(' ');
    const shard = await getJson<{ scale: number; pc: Record<string, [number, number]> }>(`${base()}/${v}/postcodes/${district}.json.gz`);
    const hit = shard?.pc[inward];
    return { results: hit ? [{ label: pc, kind: 'Postcode', lon: hit[0] / shard.scale, lat: hit[1] / shard.scale }] : [], more: false };
  }
  const [namePart, ...rest] = q.split(',');
  const key = normaliseName(namePart);
  if (!key) return { results: [], more: false };
  // "near" as in these results' own labels ("Frog Lane, near Coalpit Heath"), so a label searched again still finds it.
  const withinKey = normaliseName(rest.join(' ').replace(/^\s*near\s+/i, ''));
  const name = titleCase(key);
  const { entries, scale } = await lookUpName(v, key);
  let places = entries.map(([type, x, y, place]) => {
    const at = { lon: x / scale, lat: y / scale };
    const label = place && normaliseName(place) !== key ? `${name}, ${place}` : name;
    return { label, kind: KINDS[type] ?? 'Place', ...at, place, ...(type === 'R' ? { siteName: label } : {}) };
  });
  if (withinKey) {
    // The town is usually recorded with the street, but not always, so also keep any within a few kilometres of a place by that name.
    const near = await lookUpName(v, withinKey);
    const centres = near.entries.filter((e) => SETTLEMENTS.has(e[0])).map((e) => ({ lon: e[1] / near.scale, lat: e[2] / near.scale }));
    const distance = (p: { lat: number; lon: number }) => (centres.length ? Math.min(...centres.map((c) => metres(p, c))) : Infinity);
    places = places
      .filter((p) => normaliseName(p.place ?? '').includes(withinKey) || distance(p) <= NEAR_PLACE_M)
      .sort((a, b) => distance(a) - distance(b));
    places = places.map((p) => (p.label === name && withinKey ? { ...p, label: `${name}, near ${titleCase(withinKey)}`, ...(p.siteName ? { siteName: `${name}, ${titleCase(withinKey)}` } : {}) } : p));
  }
  return {
    results: places.slice(0, MAX_RESULTS).map(({ place: _place, ...r }) => r),
    more: places.length > MAX_RESULTS,
  };
}
